import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Award, Clock } from 'lucide-react';
import { projects, testimonials, processSteps, highlights, faqs } from '../data/mock';
import FAQAccordion from '../components/FAQAccordion';
import PartnersSection from '../components/PartnersSection';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  useScrollAnimation();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const featuredProjects = projects.slice(0, 6);
  const luxuryProjects = projects.filter(p => p.category === 'luxury-kitchen').slice(0, 2);
  const smartProjects = projects.filter(p => p.category === 'smart-kitchen').slice(0, 2);

  const navigate = useNavigate();

const scrollToContact = () => {
  navigate('/contact');
};

  return (
    <div className="bg-[#F4EFE8]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden hero-grain">
        <div 
          className="absolute inset-0 parallax-section hero-background"
          style={{
            backgroundImage: 'url(/Hero1.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/70 via-[#111111]/40 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 md:px-10 lg:px-20 py-20 sm:py-24 md:py-32 w-full">
          <div className={`max-w-2xl transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white mb-3 md:mb-4 leading-[1.1] tracking-tight">
              Precision<br />Craftsmanship
            </h1>
            <div className="w-12 sm:w-14 md:w-16 h-[1.5px] bg-[#B89A4F] mb-6 md:mb-8"></div>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white/70 mb-8 md:mb-12 font-light tracking-wide">
              Timeless Elegance
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-white/80 mb-8 md:mb-12 leading-relaxed max-w-xl">
              35+ years of engineering excellence in modular kitchens and designer wardrobes. Where precision meets artistry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
              <Link
                to="/projects"
                className="hero-btn-primary group inline-flex items-center justify-center space-x-3 bg-[#111111] text-[#B89A4F] px-8 sm:px-10 md:px-12 py-4 md:py-5 text-xs sm:text-sm tracking-[0.15em] uppercase rounded-luxury transition-all duration-300 hover:shadow-2xl"
              >
                <span>Explore Our Projects</span>
                <ArrowRight size={18} className="sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <button
                onClick={scrollToContact}
                className="hero-btn-secondary group inline-flex items-center justify-center space-x-3 border-2 border-[#B89A4F] bg-transparent text-white px-8 sm:px-10 md:px-12 py-4 md:py-5 text-xs sm:text-sm tracking-[0.15em] uppercase rounded-luxury transition-all duration-300 hover:bg-[#B89A4F] hover:text-[#111111]"
              >
                <span>Book Consultation</span>
                <ArrowRight size={18} className="sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 sm:bottom-12 left-1/2 transform -translate-x-1/2 z-20 hidden sm:flex">
          <div className="flex flex-col items-center animate-bounce-slow">
            <span className="text-[#B89A4F] text-xs tracking-[0.2em] uppercase mb-2">Scroll</span>
            <div className="w-[1px] h-12 bg-gradient-to-b from-[#B89A4F] to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Key Highlights Section */}
      <section className="py-20 lg:py-24 bg-[#111111] scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B89A4F]/10 rounded-full mb-6">
                <Shield className="text-[#B89A4F]" size={32} />
              </div>
              <h3 className="font-serif text-3xl text-white mb-2">10-Year</h3>
              <p className="text-xl text-[#B89A4F] mb-2">Structural Warranty</p>
              <p className="text-sm text-gray-400">Comprehensive warranty coverage on all installations</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B89A4F]/10 rounded-full mb-6">
                <Award className="text-[#B89A4F]" size={32} />
              </div>
              <h3 className="font-serif text-3xl text-white mb-2">20 Free</h3>
              <p className="text-xl text-[#B89A4F] mb-2">Post-Installation Services</p>
              <p className="text-sm text-gray-400">Maintenance & adjustments included first year</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B89A4F]/10 rounded-full mb-6">
                <Clock className="text-[#B89A4F]" size={32} />
              </div>
              <h3 className="font-serif text-3xl text-white mb-2">35+ Years</h3>
              <p className="text-xl text-[#B89A4F] mb-2">Experience</p>
              <p className="text-sm text-gray-400">Precision interior craftsmanship</p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Section */}
      <section className="py-20 lg:py-32 scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl text-[#111111] mb-4">
              Crafted to Your Vision
            </h2>
            <p className="text-[#242424] max-w-2xl mx-auto leading-relaxed">
              From luxury bespoke designs to value-focused solutions, we deliver exceptional quality tailored to your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Luxury Category */}
            <div className="group relative overflow-hidden bg-white rounded-luxury shadow-luxury hover:shadow-luxury-hover transition-all duration-500">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={luxuryProjects[0]?.image}
                  alt="Luxury Collection"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8 lg:p-12">
                <h3 className="font-serif text-3xl lg:text-4xl text-[#111111] mb-4">
                  Luxury Collection
                </h3>
                <p className="text-[#242424] mb-6 leading-relaxed">
                  Premium materials with precision engineering. Bespoke craftsmanship for those who demand excellence.
                </p>
                <ul className="space-y-2 mb-8">
                  <li className="flex items-center space-x-3 text-sm text-[#242424]">
                    <CheckCircle2 size={18} className="text-[#B89A4F] flex-shrink-0" />
                    <span>PU finish detailing with hand-polished surfaces</span>
                  </li>
                  <li className="flex items-center space-x-3 text-sm text-[#242424]">
                    <CheckCircle2 size={18} className="text-[#B89A4F] flex-shrink-0" />
                    <span>CNC designed panels for perfect alignment</span>
                  </li>
                  <li className="flex items-center space-x-3 text-sm text-[#242424]">
                    <CheckCircle2 size={18} className="text-[#B89A4F] flex-shrink-0" />
                    <span>Quartz countertops & German hardware systems</span>
                  </li>
                  <li className="flex items-center space-x-3 text-sm text-[#242424]">
                    <CheckCircle2 size={18} className="text-[#B89A4F] flex-shrink-0" />
                    <span>Marine plywood & HDHMR structural boards</span>
                  </li>
                </ul>
                <Link
                  to="/projects?category=luxury"
                  className="inline-flex items-center space-x-2 text-sm tracking-wider uppercase text-[#B89A4F] hover:text-[#111111] transition-colors duration-300"
                >
                  <span>Explore Luxury Projects</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Smart Budget Category */}
            <div className="group relative overflow-hidden bg-white rounded-luxury shadow-luxury hover:shadow-luxury-hover transition-all duration-500">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={smartProjects[0]?.image}
                  alt="Smart Budget Category"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8 lg:p-12">
                <h3 className="font-serif text-3xl lg:text-4xl text-[#111111] mb-4">
                  Smart Budget
                </h3>
                <p className="text-[#242424] mb-6 leading-relaxed">
                  Value-focused design with durable materials and modern aesthetics. Quality without compromise.
                </p>
                <ul className="space-y-2 mb-8">
                  <li className="flex items-center space-x-3 text-sm text-[#242424]">
                    <CheckCircle2 size={18} className="text-[#B89A4F] flex-shrink-0" />
                    <span>Matte & high gloss laminate finishes</span>
                  </li>
                  <li className="flex items-center space-x-3 text-sm text-[#242424]">
                    <CheckCircle2 size={18} className="text-[#B89A4F] flex-shrink-0" />
                    <span>Acrylic finish options</span>
                  </li>
                  <li className="flex items-center space-x-3 text-sm text-[#242424]">
                    <CheckCircle2 size={18} className="text-[#B89A4F] flex-shrink-0" />
                    <span>Smart glass & polymeric shutter solutions</span>
                  </li>
                  <li className="flex items-center space-x-3 text-sm text-[#242424]">
                    <CheckCircle2 size={18} className="text-[#B89A4F] flex-shrink-0" />
                    <span>Cost-efficient structural engineering</span>
                  </li>
                </ul>
                <Link
                  to="/projects?category=smart"
                  className="inline-flex items-center space-x-2 text-sm tracking-wider uppercase text-[#B89A4F] hover:text-[#111111] transition-colors duration-300"
                >
                  <span>Explore Smart Budget</span>
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-32 bg-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl text-[#111111] mb-4">
              Our Process
            </h2>
            <p className="text-[#242424] max-w-2xl mx-auto leading-relaxed">
              From concept to completion, excellence at every stage.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            {processSteps.map((step) => (
              <div key={step.id} className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-[#B89A4F] mb-6">
                  <span className="font-serif text-3xl text-[#B89A4F]">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#111111] mb-4">{step.title}</h3>
                <p className="text-[#242424] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 lg:py-32 scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#111111] mb-4">
                Featured Projects
              </h2>
              <p className="text-[#242424] max-w-xl leading-relaxed">
                Portfolio of completed projects showcasing craftsmanship and design excellence.
              </p>
            </div>
            <Link
              to="/projects"
              className="hidden md:inline-flex items-center space-x-2 text-sm tracking-wider uppercase text-[#B89A4F] hover:text-[#111111] transition-colors duration-300"
            >
              <span>View All Projects</span>
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project) => (
              <div key={project.id} className="group relative overflow-hidden bg-white shadow-luxury hover:shadow-luxury-hover transition-all duration-500 rounded-luxury">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <div className="text-xs tracking-widest uppercase text-[#B89A4F] mb-2">
                    {project.type}
                  </div>
                  <h3 className="text-xl font-serif text-[#111111] mb-2">{project.title}</h3>
                  <p className="text-sm text-[#242424]">{project.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 md:hidden">
            <Link
              to="/projects"
              className="inline-flex items-center space-x-2 text-sm tracking-wider uppercase text-[#B89A4F] hover:text-[#111111] transition-colors duration-300"
            >
              <span>View All Projects</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-32 bg-white scroll-animate">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl text-[#111111] mb-4">
              Client Testimonials
            </h2>
            <p className="text-[#242424] max-w-2xl mx-auto leading-relaxed">
              Homeowners who trusted us with their dream spaces.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-[#F4EFE8] p-8 lg:p-10 rounded-luxury">
                <div className="flex space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-[#B89A4F] text-xl">★</span>
                  ))}
                </div>
                <p className="text-[#242424] mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-semibold text-[#111111]">{testimonial.name}</div>
                  <div className="text-sm text-[#242424]">{testimonial.location}</div>
                  <div className="text-xs text-[#B89A4F] mt-1">{testimonial.project}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <PartnersSection />

      {/* FAQ Section */}
      <FAQAccordion faqs={faqs} />

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-[#111111] text-white scroll-animate">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl lg:text-6xl mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-lg text-gray-400 mb-12 leading-relaxed">
            Let's discuss your vision and create something extraordinary together.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-3 bg-[#111111] border-2 border-[#B89A4F] text-[#B89A4F] px-12 py-5 text-sm tracking-widest uppercase hover:bg-[#B89A4F] hover:text-[#111111] transition-all duration-300 rounded-luxury"
          >
            <span>Get in Touch</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};


export default HomePage;
